/**
 * UI state of the Library shared by the page and the Library commands (W2-10 projects-data; DESIGN.md §4.1).
 *
 * One small external store per persistence client (one per app; tests make their own): the project list as last
 * read (live projects and the Trash), the card that has focus (target of palette commands such as "Rename chart"),
 * the card being renamed, the New difficulty and "Open in another tab" dialogs, and the projects with an action
 * running. Commands and components change it through `update`; React reads it with `useLibraryState`.
 */

import { useCallback, useSyncExternalStore } from 'react';
import type { AppServices } from '../../app/registry/types';
import type { ProjectId } from '../../core/model/types';
import type { Unsubscribe } from '../../core/util/types';
import type { PersistClient, ProjectSummary } from '../../persist/types';
import type { WorkspaceId } from '../../state/types';

/** "Open in another tab" request: what was being opened and what to do once it is open here. */
export interface LockedRequest {
  readonly id: ProjectId;
  readonly name: string;
  readonly workspace?: WorkspaceId;
  /** Runs after the project opened here (export, restore a version...). */
  readonly then?: () => Promise<void> | void;
}

export interface LibraryUiState {
  /** Every project incl. the Trash, as last listed; null until the first answer. */
  readonly projects: readonly ProjectSummary[] | null;
  /** Why the last listing failed (null when it worked). */
  readonly loadError: string | null;
  /** Epoch ms of the last listing (trash days left are counted from it). */
  readonly listedAt: number;
  /** Card with keyboard focus or last clicked (target of palette commands). */
  readonly focused: ProjectId | null;
  /** Card whose title is being edited. */
  readonly renaming: ProjectId | null;
  /** Source project of the open New difficulty dialog. */
  readonly newDifficulty: ProjectId | null;
  readonly locked: LockedRequest | null;
  /** Projects with an action running (buttons wait). */
  readonly busy: ReadonlySet<ProjectId>;
  /** Library pages mounted now (dialogs fall back to system dialogs when none is). */
  readonly mounted: number;
}

const INITIAL: LibraryUiState = {
  projects: null,
  loadError: null,
  listedAt: 0,
  focused: null,
  renaming: null,
  newDifficulty: null,
  locked: null,
  busy: new Set(),
  mounted: 0,
};

export interface LibraryStore {
  get(): LibraryUiState;
  update(patch: Partial<LibraryUiState> | ((s: LibraryUiState) => Partial<LibraryUiState>)): void;
  subscribe(cb: () => void): Unsubscribe;
}

function createLibraryStore(): LibraryStore {
  let state = INITIAL;
  const listeners = new Set<() => void>();
  return {
    get: () => state,
    update(patch) {
      const p = typeof patch === 'function' ? patch(state) : patch;
      state = { ...state, ...p };
      for (const cb of [...listeners]) cb();
    },
    subscribe(cb) {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
  };
}

const stores = new WeakMap<PersistClient, LibraryStore>();

/** The Library store of an app (keyed by its persistence client, which command contexts share). */
export function libraryStore(services: Pick<AppServices, 'persist'>): LibraryStore {
  const known = stores.get(services.persist);
  if (known !== undefined) return known;
  const store = createLibraryStore();
  stores.set(services.persist, store);
  return store;
}

/** Selects from the Library state and re-renders when the selection changes. */
export function useLibraryState<T>(services: Pick<AppServices, 'persist'>, select: (s: LibraryUiState) => T): T {
  const store = libraryStore(services);
  const subscribe = useCallback((cb: () => void): Unsubscribe => store.subscribe(cb), [store]);
  const get = useCallback((): T => select(store.get()), [store, select]);
  return useSyncExternalStore(subscribe, get, get);
}

/** Marks a project busy while `work` runs. */
export async function withBusy<T>(store: LibraryStore, id: ProjectId, work: () => Promise<T>): Promise<T> {
  store.update((s) => ({ busy: new Set([...s.busy, id]) }));
  try {
    return await work();
  } finally {
    store.update((s) => {
      const next = new Set(s.busy);
      next.delete(id);
      return { busy: next };
    });
  }
}

/** Live (not trashed) projects of a listing. */
export function liveProjects(projects: readonly ProjectSummary[] | null): ProjectSummary[] {
  return (projects ?? []).filter((p) => p.trashedAt === null);
}

/** Projects in the Trash of a listing, most recently trashed first. */
export function trashedProjects(projects: readonly ProjectSummary[] | null): ProjectSummary[] {
  return (projects ?? []).filter((p) => p.trashedAt !== null).sort((a, b) => (b.trashedAt ?? 0) - (a.trashedAt ?? 0));
}
